'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { List, LayoutGrid, Rows3 } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type DataView = 'table' | 'grid' | 'compact';

interface DataViewToggleProps {
  value: DataView;
  onValueChange: (value: DataView) => void;
  pageKey: string;
  views?: DataView[];
}

const viewConfig: Record<DataView, { icon: typeof List; labelKey: string }> = {
  table: { icon: List, labelKey: 'viewTable' },
  grid: { icon: LayoutGrid, labelKey: 'viewGrid' },
  compact: { icon: Rows3, labelKey: 'viewCompact' }
};

function getStorageKey(pageKey: string) {
  return `view-preference-${pageKey}`;
}

function isDataView(value: string | null): value is DataView {
  return value === 'table' || value === 'grid' || value === 'compact';
}

// Tables are unusable on phone-width screens (horizontal scroll, tiny hit
// targets), so fall back to the grid view when no preference is stored and
// the viewport is below the `sm` breakpoint (Tailwind's 640px).
function resolveInitialDefault(defaultView: DataView): DataView {
  if (typeof window === 'undefined') return defaultView;
  if (defaultView !== 'table') return defaultView;
  return window.matchMedia('(max-width: 639px)').matches ? 'grid' : defaultView;
}

export function useDataView(
  pageKey: string,
  defaultView: DataView = 'table'
): [DataView, (view: DataView) => void] {
  const key = getStorageKey(pageKey);

  const [view, setView] = useState<DataView>(() => {
    if (typeof window === 'undefined') return defaultView;
    const stored = localStorage.getItem(key);
    if (isDataView(stored)) return stored;
    return resolveInitialDefault(defaultView);
  });

  const handleChange = useCallback(
    (newView: DataView) => {
      setView(newView);
      localStorage.setItem(key, newView);
    },
    [key]
  );

  return [view, handleChange];
}

export function DataViewToggle({
  value,
  onValueChange,
  views = ['table', 'grid', 'compact']
}: DataViewToggleProps) {
  const t = useTranslations('common');
  const activeIndex = views.indexOf(value);

  return (
    <div
      role='group'
      className='border-border bg-muted/50 relative flex items-center rounded-md border p-0.5'
    >
      {/* Sliding pill — animates between active views */}
      {activeIndex !== -1 && (
        <span
          aria-hidden
          className='bg-primary ring-border/60 pointer-events-none absolute top-0.5 bottom-0.5 w-7 rounded-sm shadow-sm ring-1'
          style={{
            insetInlineStart: `${2 + activeIndex * 28}px`,
            transition: 'inset-inline-start 180ms cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />
      )}

      {views.map((view) => {
        const { icon: Icon, labelKey } = viewConfig[view];
        const isActive = view === value;
        return (
          <Tooltip key={view}>
            <TooltipTrigger asChild>
              <button
                type='button'
                aria-pressed={isActive}
                aria-label={t(labelKey)}
                onClick={() => onValueChange(view)}
                className={cn(
                  'relative z-10 flex h-7 w-7 items-center justify-center rounded-sm transition-colors duration-150',
                  isActive
                    ? 'text-accent'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon
                  className={cn(
                    'h-3.5 w-3.5 transition-transform duration-150',
                    isActive ? 'scale-[1.08]' : 'scale-100'
                  )}
                />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t(labelKey)}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
